import React, { useState, useRef } from 'react';
import './Services.css';
import { smoothScrollTo } from '../utils/smoothScroll';
import {
  IconWeb,
  IconMobile,
  IconCloud,
  IconCode,
  IconSearch,
  IconMegaphone,
  IconCamera,
  IconVideo,
  IconDesign,
  IconChart
} from './Icons';

const ServiceCard = ({ service, onInquire }) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
    const vid = videoRef.current;
    if (vid && !videoFailed) {
      vid.play().catch(() => setVideoFailed(true));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    const vid = videoRef.current;
    if (vid) {
      vid.pause();
      vid.currentTime = 0;
    }
  };

  return (
    <div
      className="corporate-service-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Visual Banner */}
      <div className="service-media-wrapper">
        <div className="service-media-gradient" />

        {service.video && !videoFailed && (
          <video
            ref={videoRef}
            className="service-preview-video"
            loop
            muted
            playsInline
            preload="metadata"
            onCanPlay={() => setVideoReady(true)}
            onError={() => setVideoFailed(true)}
            style={{
              opacity: isHovered && videoReady ? 0.9 : 0,
              transition: 'opacity 0.5s ease',
            }}
          >
            <source src={service.video} type="video/mp4" />
          </video>
        )}

        <div className="service-media-particles">
          <span /><span /><span /><span /><span />
        </div>

        <div className="service-media-icon">
          {service.icon}
        </div>

        <span className="service-category-tag">{service.categoryShort}</span>
      </div>

      <div className="service-card-body">
        <h3 className="service-card-title">{service.title}</h3>
        <p className="service-card-desc">{service.description}</p>

        <ul className="service-features-list">
          {service.features.map((feat, idx) => (
            <li key={idx} className="service-feature-item">
              <span className="feature-check">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
              <span>{feat}</span>
            </li>
          ))}
        </ul>

        <div className="service-card-footer">
          <button className="service-inquire-btn" onClick={() => onInquire(service.title)}>
            <span>Inquire Solution</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const [activeTab, setActiveTab] = useState('All');

  const categories = ['All', 'Engineering & Cloud', 'Growth & Performance Media', 'Strategy & Design'];

  const services = [
    {
      icon: <IconWeb size={28} />,
      title: 'Web Application Engineering',
      category: 'Engineering & Cloud',
      categoryShort: 'Engineering',
      video: '/videos/web.mp4',
      description: 'Fast, scalable web platforms built with React & Node.js. Optimized for ultra-fast load times, SEO, and high user traffic.',
      features: ['Modern React & Next.js Stacks', 'Instant Page Loads & SSR', 'Clean API & Backend Setup', 'Enterprise-Level Security'],
    },
    {
      icon: <IconMobile size={28} />,
      title: 'Mobile App Engineering',
      category: 'Engineering & Cloud',
      categoryShort: 'Mobile',
      video: '/videos/app.mp4',
      description: 'Smooth iOS & Android apps engineered for fluid 60fps performance, offline reliability, and intuitive user experiences.',
      features: ['Cross-Platform React Native', 'Secure In-App Payments', 'Real-Time Chat & Push Alerts', 'App Store & Play Store Publishing'],
    },
    {
      icon: <IconCloud size={28} />,
      title: 'Cloud Infrastructure & DevOps',
      category: 'Engineering & Cloud',
      categoryShort: 'Cloud',
      video: '/videos/cloud.mp4',
      description: 'Hassle-free cloud setups on AWS, GCP, or Vercel. Automated deployment pipelines and auto-scaling backends that stay up 24/7.',
      features: ['AWS / GCP / Vercel Management', 'Docker Container Setup', 'Automated CI/CD Workflows', 'Database Optimization & Backups'],
    },
    {
      icon: <IconCode size={28} />,
      title: 'End-to-End Product Development',
      category: 'Engineering & Cloud',
      categoryShort: 'Product',
      video: '/videos/product.mp4',
      description: 'From rapid initial prototype to full commercial launch. We build MVPs fast and scale your product roadmap with clean releases.',
      features: ['Rapid MVP to Scale', 'Agile Weekly Deliveries', 'Automated Testing & QA', 'Continuous Feature Upgrades'],
    },
    {
      icon: <IconSearch size={28} />,
      title: 'Technical SEO & Search Dominance',
      category: 'Growth & Performance Media',
      categoryShort: 'SEO',
      video: '/videos/seo.mp4',
      description: 'Get your business ranked on top of Google search and local maps with high-intent content and technical speed optimizations.',
      features: ['Core Web Vitals Speed Audit', 'High-Intent Search Content', 'Local Map & Business SEO', 'Competitor Ranking Takeover'],
    },
    {
      icon: <IconMegaphone size={28} />,
      title: 'Meta & Performance Ad Campaigns',
      category: 'Growth & Performance Media',
      categoryShort: 'Ads',
      video: '/videos/meta.mp4',
      description: 'High-converting ad funnels across Instagram, Facebook, and Google designed to bring you real paying customers, not empty clicks.',
      features: ['Laser Audience Targeting', 'Retargeting Conversion Funnels', 'Creative A/B Copy Testing', 'Deep Conversion Analytics'],
    },
    {
      icon: <IconCamera size={28} />,
      title: 'Commercial Brand Photography',
      category: 'Growth & Performance Media',
      categoryShort: 'Photography',
      video: '/videos/photography.mp4',
      description: 'High-impact product photography, corporate team portraits, and industrial visuals that elevate your brand image.',
      features: ['Studio Product Shoots', 'Executive Leadership Portraits', 'Industrial & Architecture Media', 'High-Res Retouching & Grading'],
    },
    {
      icon: <IconVideo size={28} />,
      title: 'Brand Videography & Social Reels',
      category: 'Growth & Performance Media',
      categoryShort: 'Video',
      video: '/videos/videography.mp4',
      description: 'Cinematic corporate brand films, short-form social media reels, and product demos crafted to grab attention and convert.',
      features: ['4K Cinematic Production', 'Post-Production & Editing', 'Engaging Social Reels', 'Multi-Platform Video Exports'],
    },
    {
      icon: <IconDesign size={28} />,
      title: 'UI/UX Product Design',
      category: 'Strategy & Design',
      categoryShort: 'UI/UX',
      video: '/videos/ui.mp4',
      description: 'Human-centered digital design and interactive prototypes that make complex software workflows simple and enjoyable to use.',
      features: ['Interactive Figma Prototypes', 'User Journey Mapping', 'Design System Libraries', 'Conversion UX Optimization'],
    },
    {
      icon: <IconChart size={28} />,
      title: 'Strategic Technology Advisory',
      category: 'Strategy & Design',
      categoryShort: 'Consulting',
      video: '/videos/consultancy.mp4',
      description: 'Senior engineering advice on tech stack selection, cloud cost reduction, and modernizing legacy codebases without risk.',
      features: ['Unbiased Tech Stack Guidance', 'Architecture Scalability Audit', 'Cloud Spend Optimization', 'Clear Product Roadmaps'],
    },
  ];

  const handleInquire = () => smoothScrollTo('contact', 85, 1100);

  const filteredServices = activeTab === 'All'
    ? services
    : services.filter(s => s.category === activeTab);

  return (
    <section id="services" className="services">
      <div className="services-container">
        <div className="section-header">
          <div className="section-badge">
            <span>What We Build</span>
          </div>
          <h2 className="section-title">Everything You Need to Build, Launch & Scale</h2>
          <p className="section-subtitle">
            From web & mobile apps to cloud backends and performance marketing — engineered by hands-on developers who care about quality.
          </p>
        </div>

        <div className="services-tabs">
          {categories.map((cat, idx) => (
            <button
              key={idx}
              className={`services-tab-btn ${activeTab === cat ? 'active' : ''}`}
              onClick={() => setActiveTab(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="corporate-services-grid">
          {filteredServices.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              onInquire={handleInquire}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
